import { Center, Fade, Flex, Spinner, Text, useToast } from '@chakra-ui/react';
import { useState, useMemo, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useDebounce } from 'use-debounce';
import { StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

import { availableStates } from '@duke-hq/svc-api/lib/states';
import { StatesFilter } from '@duke-hq/filters/components/StatesFilter';
import { EnabledFilterSiteLocation } from '@duke-hq/filters/components/EnabledFilterSiteLocation';
import { LocationTypeFilter } from '@duke-hq/filters/components/LocationTypeFilter';
import { SearchSiteLocation } from '@duke-hq/filters/components/SearchSiteLocation';
import { SiteList, SiteLocationType } from '@duke-hq/ui/components/SiteList/SiteList';

export function TeamMemberSiteAccess({
  member,
}: {
  member: NonNullable<RouterOutputs['users']['consultantById']>;
}) {
  const [search, setSearch] = useState<string>('');
  const [showFilter, setShowFilter] = useState<string | null>(null);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const utils = trpcTenant.useUtils();

  const toast = useToast();
  const [debouncedSearch] = useDebounce(search, 500);

  const createUserSiteAccess = trpcTenant.locations.createUserSiteAccess.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Site access added successfully`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });

      await utils.locations.listAccessibleSiteLocations.refetch();
    },
  });

  const deleteUserSiteAccess = trpcTenant.locations.deleteUserSiteAccess.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Site access removed successfully`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });

      await utils.locations.listAccessibleSiteLocations.refetch();
    },
    onError: (error) => {
      toast({
        title: error.message,
        status: 'error',
        isClosable: true,
        duration: 3000,
      });
    },
  });

  const { data, isPending } = trpcTenant.locations.listAccessibleSiteLocations.useQuery(
    {
      search: debouncedSearch,
      filters: {
        location:
          locationFilter === 'Sites' ? 'site' : locationFilter === 'Regions' ? 'region' : undefined,
        available:
          showFilter === 'Show enabled' ? true : showFilter === 'Show disabled' ? false : undefined,
        state: StatesSchema.safeParse(stateFilter).data,
      },
      consultantId: member.id,
    },
    {
      enabled: Boolean(member.id),
    },
  );

  const toggleSiteAvailability = useCallback(
    (site: SiteLocationType) => {
      if (!site.assigned) {
        createUserSiteAccess.mutate({
          consultantId: member.id,
          regionId: site.type === 'region' ? site.id : undefined,
          siteId: site.type === 'site' ? site.id : undefined,
        });
      } else {
        deleteUserSiteAccess.mutate({
          consultantId: member.id,
          regionId: site.type === 'region' ? site.id : undefined,
          siteId: site.type === 'site' ? site.id : undefined,
        });
      }
    },
    [createUserSiteAccess, deleteUserSiteAccess, member.id],
  );

  const sites = useMemo(() => {
    if (typeof data === 'undefined') {
      return [];
    }

    return data.map(
      (site) =>
        ({
          assigned: site.assigned,
          id: site.id,
          name: site.name,
          // TODO: move this parse to the API
          state: StatesSchema.safeParse(site.state).data,
          country: null,
          description: site.description,
          type: site.type,
          count: site.count,
        }) satisfies SiteLocationType,
    );
  }, [data]);

  return (
    <>
      <Flex
        w="full"
        gap={2}
        p={4}
        style={{ position: 'sticky', top: '0', zIndex: '1' }}
        bg="bg.canvas"
      >
        <EnabledFilterSiteLocation
          showFilter={showFilter}
          setShowFilter={setShowFilter}
          options={[
            { value: 'Show enabled', label: t`Show enabled` },
            { value: 'Show disabled', label: t`Show disabled` },
          ]}
          defaultLabel={t`Show all`}
        />
        <LocationTypeFilter
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          options={[
            { value: 'Sites', label: t`Sites` },
            { value: 'Regions', label: t`Regions` },
          ]}
          defaultLabel={t`Location`}
        />
        <StatesFilter
          stateFilter={stateFilter}
          stateOptions={availableStates}
          setStateFilter={setStateFilter}
        />
        <SearchSiteLocation search={search} setSearch={setSearch} placeholder={t`Search`} />
      </Flex>

      {!isPending && sites?.length === 0 && (
        <Center p={8}>
          <Text textAlign="center">
            <Trans>No sites found</Trans>
          </Text>
        </Center>
      )}

      {Boolean(isPending && !sites) && (
        <Fade
          in={true}
          transition={{
            enter: {
              delay: 0.3,
              duration: 0,
            },
          }}
        >
          <Center p={8}>
            <Spinner size="xl" transitionDelay={'2900ms'} />
          </Center>
        </Fade>
      )}

      {sites ? <SiteList sites={sites} toggleSiteAvailability={toggleSiteAvailability} /> : null}
    </>
  );
}
